import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { MODALS } from "../../../config/params";
import { PAGES_PATH } from "../../../navigation/params";
import { useModal } from "../../../context/ModalContext";
import type { IWellnessPlanErrorPageCta } from "./WellnessPlanErrorPage";
import type { WellnessPlanErrorCtaKey } from "./wellnessPlanError.helpers";

interface IUseWellnessPlanErrorCtaParams {
  petId?: string | null;
  onTryAgain?: () => void;
}

/**
 * Builds the primary CTA descriptor for the unified wellness error screen from
 * a CTA key. Centralizes the navigation / modal / callback wiring so every
 * container renders the same actions for the same error.
 *
 * @param {IUseWellnessPlanErrorCtaParams} params - Runtime CTA handlers.
 * @return {Function} Resolver mapping a CTA key to a concrete CTA descriptor.
 */
export const useWellnessPlanErrorCta = ({
  petId,
  onTryAgain,
}: IUseWellnessPlanErrorCtaParams) => {
  const navigate = useNavigate();
  const { openModal } = useModal();

  return useCallback(
    (key: WellnessPlanErrorCtaKey): IWellnessPlanErrorPageCta | undefined => {
      switch (key) {
      case "EXPLORE_PLAN":
        return {
          label: "Explore Plan",
          onClick: () =>
            navigate(
              `${PAGES_PATH.PET_CARE}?id=${petId ?? ""}&category=TotalCare_Plan`,
            ),
        };
      case "GO_TO_ACCOUNT":
        return {
          label: "Go to Account",
          onClick: () => navigate(PAGES_PATH.USER_PROFILE),
        };
      case "ADD_PET":
        return {
          label: "Add Pet", onClick: () => openModal(MODALS.ADD_A_PET, "", {
            redirectOnSave: true,
          }) };
      case "SELECT_A_PLAN":
        return {
          label: "Select a Plan",
          onClick: () =>
            navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${petId ?? ""}`),
        };
      case "TRY_AGAIN":
        return onTryAgain ?
          { label: "Try Again", onClick: onTryAgain } :
          undefined;
      case "NONE":
      default:
        return undefined;
      }
    },
    [navigate, openModal, petId, onTryAgain],
  );
};
